import type { TFunction } from "i18next";
import { COMPETITOR_PATH_TOKEN, brandIconUrl, relocateAdgepublicAssetUrl } from "@nexus/contracts";

import {
  humanizeConceptLabel,
  humanizePersonaCompetitor,
  humanizePersonaSentence,
  humanizePersonaTitle,
  personaInitials,
} from "@/features/personas/lib/persona-format";
import { readCachedCreativeUrlSettings } from "@/lib/queries/system-settings.queries";

import { PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS } from "../constants/persona-opportunity-tuning-defaults";
import {
  readPostFlightMetrics,
  type PostFlightOpportunityMetrics,
} from "./post-flight-opportunity-metrics";
import type {
  PersonaOpportunityRow,
  PersonaOpportunitySetDetail,
  PersonaOpportunityStatus,
} from "../api/persona-opportunity-sets.api";

/**
 * Opportunity-level view model (KTD9) — one shared shape for Cards + Table.
 * Built from `PersonaOpportunitySetDetail`; never invents Strength / drivingFeatureScore.
 */

export type PersonaOpportunityDrivenBy = "painpoint" | "motivation";

export interface PersonaOpportunityDisplayRow {
  id: string;
  personaId: string;
  status: PersonaOpportunityStatus;
  angle: string;
  painPoint: string | null;
  motivation: string | null;
  /** Which feature type scored this angle — drives Why fallback + UI highlight. */
  drivenBy: PersonaOpportunityDrivenBy | null;
  /** v5 Why sentence built from investment + competitor + stick KPIs. */
  why: string;
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number;
  duplicatedPct: number;
  myInvestmentSharePct: number;
  marketInvestmentSharePct: number;
  /** Null when the backend omitted the multiple — UI shows an em dash. */
  investmentIndex: number | null;
  thumbnailUrl: string | null;
  /** Supporting-ad creative URL (video / media). */
  mediaUrl: string | null;
  daysLive: number | null;
  /** Supporting-ad duplication count from generation snapshot. */
  duplications: number | null;
  /** Supporting-ad brand slug humanized for display (`first_aid_beauty` → `First Aid Beauty`). */
  competitorName: string | null;
  /** First letter of the brand for the avatar fallback when no logo URL is stored. */
  competitorInitial: string | null;
  /** Competitor logo from BQ `brands_metadata.profile_picture_url` when present. */
  competitorProfilePictureUrl: string | null;
  rank: number;
  /** Post-Flight lineage, for example `from: Parent title`. Absent on other set types. */
  fromLabel?: string | null;
  /** Winning-ad metrics. Set only for Post-Flight rows. */
  postFlight?: PostFlightOpportunityMetrics | null;
}

export interface PersonaOpportunityPersonaGroup {
  personaId: string;
  title: string;
  who: string | null;
  initials: string;
  avatarUrl: string | null;
  personalityKey: string;
  pains: string[];
  motivations: string[];
  /** True once BigQuery stopped returning this persona — the group renders with a badge. */
  isStale: boolean;
  opportunities: PersonaOpportunityDisplayRow[];
}

/** Opportunities-tab model — all opportunity rows for the set (every status). */
export interface PersonaOpportunityOpportunitiesModel {
  groups: PersonaOpportunityPersonaGroup[];
  personaCount: number;
  angleCount: number;
  competitorsTotal: number;
}

/** @deprecated Use `PersonaOpportunityOpportunitiesModel`. */
export type PersonaOpportunityActiveModel = PersonaOpportunityOpportunitiesModel;

/**
 * Design Why line:
 * "The market invests 15× more here than you do — 7 of 12 competitors run it,
 * and 68% of those ads stick past 14 days."
 * When investment index is missing, lead with a no-investment sentence keyed off `drivenBy`.
 */
export function buildPersonaOpportunityWhyLine(
  input: {
    investmentIndex: number | null;
    competitorsRunningCount: number;
    competitorsTotal: number;
    stickRatePct: number;
    stickDays: number;
    drivenBy: PersonaOpportunityDrivenBy | null;
  },
  t: TFunction,
): string {
  const competitors = formatCompetitorsOfTotal(
    input.competitorsRunningCount,
    input.competitorsTotal,
  );
  const stickRate = formatPercent(input.stickRatePct);
  const trailing = t("personaOpportunities.active.why.trailing", {
    competitors,
    stickRate,
    stickDays: input.stickDays,
    defaultValue:
      "{{competitors}} competitors run it, and {{stickRate}} of those ads stick past {{stickDays}} days.",
  });

  const multiple = formatInvestmentMultiple(input.investmentIndex);
  if (multiple) {
    return t("personaOpportunities.active.why.withIndex", {
      multiple,
      trailing,
      defaultValue: "The market invests {{multiple}} here than you do — {{trailing}}",
    });
  }

  if (input.drivenBy === "motivation") {
    return t("personaOpportunities.active.why.noInvestmentMotivation", {
      trailing,
      defaultValue: "You don't invest in this motivation at all — {{trailing}}",
    });
  }

  if (input.drivenBy === "painpoint") {
    return t("personaOpportunities.active.why.noInvestmentPainPoint", {
      trailing,
      defaultValue: "You don't invest in this pain point at all — {{trailing}}",
    });
  }

  return t("personaOpportunities.active.why.noInvestmentAngle", {
    trailing,
    defaultValue: "You don't invest in this angle at all — {{trailing}}",
  });
}

function toDisplayRow(
  opportunity: PersonaOpportunityRow,
  competitorsTotal: number,
  stickDays: number,
  t: TFunction,
  isPostFlight: boolean,
): PersonaOpportunityDisplayRow {
  const { kpis, supportingAd } = opportunity;
  const paths = readCachedCreativeUrlSettings();
  const competitorRaw = supportingAd?.competitor?.trim() || null;
  const competitorName = competitorRaw ? humanizePersonaCompetitor(competitorRaw) : null;
  const drivenBy = opportunity.drivenBy;
  const investmentIndex = kpis.investmentIndex ?? null;
  const postFlight = isPostFlight ? readPostFlightMetrics(opportunity.metadata) : null;
  const fromSource = opportunity.feature?.trim() || postFlight?.parentTitle || "";

  return {
    id: opportunity.id,
    personaId: opportunity.personaId,
    status: opportunity.status,
    angle: humanizePersonaTitle(opportunity.angle),
    painPoint: opportunity.painPoint ? humanizePersonaSentence(opportunity.painPoint) : null,
    motivation: opportunity.motivation ? humanizePersonaSentence(opportunity.motivation) : null,
    drivenBy,
    why: isPostFlight
      ? (opportunity.topicDescription?.trim() ?? "")
      : buildPersonaOpportunityWhyLine(
          {
            investmentIndex,
            competitorsRunningCount: kpis.competitorsRunningCount,
            competitorsTotal,
            stickRatePct: kpis.stickRatePct,
            stickDays,
            drivenBy,
          },
          t,
        ),
    fromLabel: isPostFlight && fromSource ? humanizeConceptLabel(fromSource) : null,
    postFlight,
    creativesCount: kpis.creativesCount,
    competitorsRunningCount: kpis.competitorsRunningCount,
    competitorsTotal,
    stickRatePct: kpis.stickRatePct,
    duplicatedPct: kpis.duplicatedPct,
    myInvestmentSharePct: kpis.myInvestmentSharePct,
    marketInvestmentSharePct: kpis.marketInvestmentSharePct,
    investmentIndex,
    thumbnailUrl: relocateAdgepublicAssetUrl(supportingAd?.thumbnailUrl, paths),
    mediaUrl: relocateAdgepublicAssetUrl(supportingAd?.url, paths),
    daysLive: supportingAd?.daysLive ?? null,
    duplications: supportingAd?.duplications ?? null,
    competitorName,
    competitorInitial: competitorName ? competitorName.charAt(0).toUpperCase() : null,
    competitorProfilePictureUrl: brandIconUrl(paths.competitorsPath, COMPETITOR_PATH_TOKEN, competitorRaw),
    rank: opportunity.rank,
  };
}

/**
 * Maps set detail → persona groups for the Opportunities tab.
 * Includes **all** opportunity statuses; sort within group is applied by the filter layer.
 */
export function toPersonaOpportunityOpportunitiesModel(
  detail: PersonaOpportunitySetDetail,
  t: TFunction,
): PersonaOpportunityOpportunitiesModel {
  const competitorsTotal = detail.set.competitorsTotal;
  const stickDays = detail.set.config?.stickDays ?? PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS;
  const isPostFlight = detail.set.type === "POST_FLIGHT";
  const groups: PersonaOpportunityPersonaGroup[] = detail.personas.map((section) => {
    const title = humanizePersonaTitle(section.title);
    const opportunities = [...section.opportunities]
      .filter((opportunity) => opportunity.isCustom !== true)
      .sort((a, b) => a.rank - b.rank)
      .map((opportunity) => toDisplayRow(opportunity, competitorsTotal, stickDays, t, isPostFlight));

    return {
      personaId: section.personaId,
      title,
      who: section.who ? humanizePersonaSentence(section.who) : null,
      initials: personaInitials(title),
      avatarUrl: section.avatarUrl ?? null,
      personalityKey: section.personalityKey ?? section.personaId,
      pains: section.pains.map(humanizePersonaSentence),
      motivations: section.motivations.map(humanizePersonaSentence),
      isStale: section.staledAt !== null,
      opportunities,
    };
  });

  groups.sort((a, b) => a.title.localeCompare(b.title));

  const angleCount = groups.reduce((total, group) => total + group.opportunities.length, 0);

  return {
    groups,
    personaCount: groups.length,
    angleCount,
    competitorsTotal,
  };
}

/** @deprecated Use `toPersonaOpportunityOpportunitiesModel`. */
export const toPersonaOpportunityActiveModel = toPersonaOpportunityOpportunitiesModel;

export function regroupPersonaOpportunityOpportunitiesModel(
  model: PersonaOpportunityOpportunitiesModel,
  groupBy: "persona" | "angle" | "set",
  setTitle: string,
): PersonaOpportunityOpportunitiesModel {
  if (groupBy === "persona") return model;

  const flat = model.groups.flatMap((group) => group.opportunities);

  if (groupBy === "set") {
    return {
      ...model,
      groups: [
        {
          personaId: "set",
          title: setTitle,
          who: null,
          initials: personaInitials(setTitle),
          avatarUrl: null,
          personalityKey: "set",
          pains: [],
          motivations: [],
          isStale: false,
          opportunities: [...flat].sort((a, b) => a.rank - b.rank || a.angle.localeCompare(b.angle)),
        },
      ],
      personaCount: 1,
    };
  }

  const byAngle = new Map<string, PersonaOpportunityDisplayRow[]>();
  for (const row of flat) {
    const key = row.angle.trim() || "—";
    const list = byAngle.get(key) ?? [];
    list.push(row);
    byAngle.set(key, list);
  }

  const groups = [...byAngle.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([angle, opportunities]) => ({
      personaId: angle,
      title: angle,
      who: null,
      initials: personaInitials(angle),
      avatarUrl: null,
      personalityKey: angle,
      pains: [],
      motivations: [],
      isStale: false,
      opportunities: [...opportunities].sort((a, b) => a.rank - b.rank),
    }));

  return {
    ...model,
    groups,
    personaCount: groups.length,
  };
}

/**
 * KPI rates from the persona-angles pipeline are fractional (0–1).
 * Multiply by 100 only at display (see PersonaAngleRowSchema).
 */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

export function formatCompetitorsOfTotal(running: number, total: number): string {
  return `${running} of ${total}`;
}

/** e.g. `15× more` — returns null when index is missing so callers can show "—". */
export function formatInvestmentMultiple(index: number | null | undefined): string | null {
  if (index == null || !Number.isFinite(index)) return null;
  const rounded = Number.isInteger(index) ? String(index) : index.toFixed(1).replace(/\.0$/, "");
  return `${rounded}× more`;
}
